import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Checkbox, Modal, TextInput } from "@opendate-app/setlist";
import { useSeriesShowContext } from "../SeriesShowApp";
import { formatVenueDate } from "../../../shared/venueZoneFormatting";

const EMPTY_RECIPIENT = { firstName: "", lastName: "", email: "", quantity: "1", reason: "" };
const NOT_ISSUED_MESSAGE = "This comp wasn't issued. Reload the page and try again.";
const UNCONFIRMED_MESSAGE = "We couldn't confirm whether this comp went through. Check the Sold count before trying again.";

const failureMessage = (e) => {
  const apiErrors = e?.response?.data?.errors;
  if (Array.isArray(apiErrors) && apiErrors.length > 0) return apiErrors.join(". ");

  const status = e?.response?.status;
  if (status >= 400 && status < 500) return NOT_ISSUED_MESSAGE;

  return UNCONFIRMED_MESSAGE;
};

const ComplimentaryPassModal = ({ product, onClose }) => {
  const { handleIssueComplimentaryPass } = useSeriesShowContext();
  const [recipient, setRecipient] = useState(EMPTY_RECIPIENT);
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [issued, setIssued] = useState(null);

  useEffect(() => {
    if (!product) return;
    setRecipient(EMPTY_RECIPIENT);
    setSendEmail(true);
    setSubmitting(false);
    setError(null);
    setIssued(null);
  }, [product?.id]);

  const updateRecipient = (field) => (e) => setRecipient((current) => ({ ...current, [field]: e.target.value }));

  const isValid = recipient.firstName.trim() !== ""
    && recipient.lastName.trim() !== ""
    && recipient.email.trim() !== ""
    && Number(recipient.quantity) >= 1;

  const submit = async () => {
    if (submitting || !isValid) return;
    try {
      setSubmitting(true);
      setError(null);
      setIssued(null);
      const result = await handleIssueComplimentaryPass(product.id, {
        first_name: recipient.firstName.trim(),
        last_name: recipient.lastName.trim(),
        email: recipient.email.trim(),
        quantity: recipient.quantity,
        comp_reason: recipient.reason.trim(),
        should_send_ticket_email: sendEmail,
      });
      if (!result?.id) {
        setError(NOT_ISSUED_MESSAGE);
        return;
      }
      setIssued(result);
      setRecipient(EMPTY_RECIPIENT);
    } catch (e) {
      setError(failureMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={product !== null}
      onClose={onClose}
      closeOnEsc={!submitting}
      closeOnBackdrop={!submitting}
      showClose={!submitting}
      icon={false}
      title={product ? `Comp ${product.name}` : ""}
      footer={(
        <>
          <Button onClick={onClose} disabled={submitting} data-testid="complimentary-pass-done">Done</Button>
          <Button
            variant="primary"
            data-testid="complimentary-pass-submit"
            onClick={submit}
            disabled={submitting || !isValid}
          >
            {submitting ? "Comping…" : "Comp pass"}
          </Button>
        </>
      )}
    >
      <div data-testid="complimentary-pass-modal">
        <p className="text-muted mb-3">
          {product
            ? `Issue passes at no charge. ${product.sellable_remaining} left. The recipient sees them in their account under this email.`
            : ""}
        </p>
        {product?.delivery_status === "Scheduled" && product.delivery_time && (
          <p className="text-muted mb-3" data-testid="complimentary-pass-delivery">
            {`Delivery is scheduled for ${formatVenueDate(product.delivery_time)}, so the recipient can't see the pass until then.${sendEmail ? " They're emailed when it's ready." : ""}`}
          </p>
        )}
        {issued && (
          <div className="mb-3" data-testid="complimentary-pass-issued">
            <Alert
              color="success"
              role="status"
              title={`Comped ${issued.quantity} to ${issued.email}. Enter the next person, or select Done.`}
            />
          </div>
        )}
        {error && (
          <div className="mb-3" data-testid="complimentary-pass-error">
            <Alert color="error" role="alert" title={error} />
          </div>
        )}
        <div className="d-flex mb-3">
          <div className="flex-fill mr-2">
            <TextInput
              label="First name"
              id="complimentary-pass-first-name"
              data-testid="complimentary-pass-first-name"
              maxLength={255}
              value={recipient.firstName}
              onChange={updateRecipient("firstName")}
            />
          </div>
          <div className="flex-fill">
            <TextInput
              label="Last name"
              id="complimentary-pass-last-name"
              data-testid="complimentary-pass-last-name"
              maxLength={255}
              value={recipient.lastName}
              onChange={updateRecipient("lastName")}
            />
          </div>
        </div>
        <div className="mb-3">
          <TextInput
            label="Email"
            id="complimentary-pass-email"
            data-testid="complimentary-pass-email"
            type="email"
            maxLength={255}
            value={recipient.email}
            onChange={updateRecipient("email")}
          />
        </div>
        <div className="mb-3">
          <TextInput
            label="Quantity"
            id="complimentary-pass-quantity"
            data-testid="complimentary-pass-quantity"
            type="number"
            min="1"
            step="1"
            onWheel={(e) => e.currentTarget.blur()}
            value={recipient.quantity}
            onChange={updateRecipient("quantity")}
          />
        </div>
        <div className="mb-3">
          <TextInput
            label="Reason (optional)"
            id="complimentary-pass-reason"
            data-testid="complimentary-pass-reason"
            maxLength={255}
            placeholder="e.g. Migrated membership"
            hint="Only your team sees this."
            value={recipient.reason}
            onChange={updateRecipient("reason")}
          />
        </div>
        <Checkbox
          id="complimentary-pass-send-email"
          data-testid="complimentary-pass-send-email"
          label="Email the recipient a confirmation"
          checked={sendEmail}
          onChange={(e) => setSendEmail(e.target.checked)}
        />
        <p className="text-muted small mt-1 mb-0">
          Automations you&apos;ve set up for pass purchases still run either way.
        </p>
      </div>
    </Modal>
  );
};

ComplimentaryPassModal.propTypes = {
  product: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default ComplimentaryPassModal;
